import { Component, OnInit } from '@angular/core';
import {
  StepType,
  RegistrationStepQuery,
  LoggingService,
  ErrorStateService,
  RegistrationStepService,
  ChildRegistrationModel,
  MemberQuery,
  MemberDetails,
  ChildQuery,
} from '@fgb/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'fgb-registration-add-children',
  templateUrl: './registration-add-children.component.html',
  styleUrls: ['./registration-add-children.component.scss'],
})
export class RegistrationAddChildrenComponent implements OnInit {
  readonly stepType = StepType.AddChild;
  hasSubmitted: boolean = false;
  today = new Date();
  childForms: FormGroup[] = [];
  minAge = 0;
  maxAge = 12;
  childLimit = 10;
  currentUser: MemberDetails | undefined;

  constructor(
    private registrationStepQuery: RegistrationStepQuery,
    private registrationStepService: RegistrationStepService,
    private logService: LoggingService,
    public errorService: ErrorStateService,
    private formBuilder: FormBuilder,
    private memberQuery: MemberQuery,
    private childQuery: ChildQuery
  ) {}

  ngOnInit() {
    const step = this.registrationStepQuery.getStep(this.stepType);
    if (!step) {
      this.logService.warning('No step data found in the store for step', this.stepType);
      this.childForms.push(this._createChildForm());
      return;
    } else {
      this.logService.info('Prefilling existing step data');
      this._prefillExistingStepData(step.Data as ChildRegistrationModel[]);
    }

    this.memberQuery
      .selectMemberDetails()
      .subscribe((md) => {
        this.currentUser = md;

        let currentNumberofChildren: number = 0;
        this.childQuery
          .selectChildren()
          .pipe(
            tap((x) => {
              currentNumberofChildren = x.length;
            })
          )
          .toPromise();

        this.childLimit = this.childLimit - currentNumberofChildren;
      })
      .unsubscribe();
  }

  addChild() {
    this.childForms.push(this._createChildForm());
  }

  removeChild(index: number) {
    this.childForms.splice(index, 1);
  }

  submitStep() {
    this.logService.info('Attempting step submission.');
    this.hasSubmitted = true;

    if (this.childForms.some((x) => !x.valid)) {
      this.errorService.clearErrors();
      this.errorService.addError('Some fields are invalid.');
      this.logService.info('Validation failed.', this.childForms);
      return;
    }

    this.logService.info('Validation passed.', this.childForms);
    let stepData: ChildRegistrationModel[] = [];

    this.childForms.forEach((x) => {
      let childDetails = {
        ...x.value,
        DateOfBirth: this._getStringFromNgbDate(x.get('DateOfBirth')!.value),
      } as ChildRegistrationModel;

      stepData.push(childDetails);
    });

    this.logService.info('Submitting step data:', stepData);
    this.registrationStepService.setStepCompleteStatus(StepType.MembershipOption, false);
    this.registrationStepService.submitRegistrationStepData(this.stepType, stepData).toPromise();
  }

  /** Prefills the given step data into the form control values. */
  private _prefillExistingStepData(stepData?: ChildRegistrationModel[]) {
    if (stepData && stepData.length > 0) {
      stepData.forEach((child, i) => {
        this.childForms.push(this._createChildForm());
        for (const [key, value] of Object.entries(child)) {
          const control = this.childForms[i].get(key);
          if (control) {
            if (key === 'DateOfBirth') {
              control.setValue(this._getNgbDateFromString(value));
            } else {
              control.setValue(value);
            }
          }
        }
      });
    } else {
      this.logService.info('No existing children, creating form');
      this.childForms.push(this._createChildForm());
    }
  }

  private _createChildForm(): FormGroup {
    return this.formBuilder.group({
      Firstname: ['', Validators.required],
      Lastname: ['', Validators.required],
      DateOfBirth: ['', Validators.required],
      Barcode: '',
    });
  }

  /** Gets a date string in ISO format from an NgbDate. */
  private _getStringFromNgbDate(ngbDate: NgbDate) {
    if (ngbDate) {
      let date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day, 12);
      date.setUTCHours(0);
      date.setUTCMinutes(0);
      date.setUTCSeconds(0);
      return date.toISOString();
    } else {
      return '';
    }
  }

  /** Gets an NgbDate from a date string. */
  private _getNgbDateFromString(dateString: string): NgbDate | null {
    if (dateString) {
      const date = new Date(dateString);
      if (date) {
        return new NgbDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
      }
    }

    return null;
  }
}
